import {
  GetLocationsLossesResponse,
  GetLossesAverageResponse,
  GetLossesBalanceResponse,
  GetStatsLossesInfoResponse,
  lossesAverageParams,
  lossesBalanceParams,
  lossesChartParams,
  lossesLocationParams,
  lossesStatsParams,
} from '../models/losses.models';
import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ErrorHandlerService } from '@errorHandler';
import { HttpParams } from '@angular/common/http';
import {
  GET_LOSSES_AVERAGE_ENDPOINT,
  GET_LOSSES_CHART_ENDPOINT,
  GET_LOSSES_ENDPOINT,
  GET_LOSSES_LOCATION_ENDPOINT,
  GET_LOSSES_STATS_ENDPOINT,
} from '../constants/endpoints.constants';
import { withSpinner } from '../../app/core/services/spinner.controller';
import { Filter } from '../models/common.models';
import { GetCenterChartLossesDataItems } from '../models/losses-chart.models';

type QueryValue =
  | string
  | number
  | boolean
  | readonly (string | number | boolean)[];

const NO_CENTER = 'undefined'; // valor que espera el backend cuando no hay centro

@Injectable({ providedIn: 'root' })
export class DataLossesService {
  protected readonly apiService = inject(ApiService);
  protected readonly errorService = inject(ErrorHandlerService);

  getLossesAverageData(params: lossesAverageParams) {
    return this.apiService.get<GetLossesAverageResponse>(
      GET_LOSSES_AVERAGE_ENDPOINT,
      this.toHttpParams({
        ...params,
        centerId: params?.centerId || NO_CENTER,
      }),
    );
  }

  getBalanceLosses(params: lossesBalanceParams) {
    const {
      dateFrom,
      dateTo,
      filters,
      query,
      orderBy,
      order,
      pageIndex,
      pageSize,
      reliabilityThreshold,
      origin,
    } = params ?? ({} as Partial<lossesBalanceParams>);

    const queryParams = this.toHttpParams({
      dateFrom,
      dateTo,
      origin,
      reliabilityThreshold,
      orderBy: orderBy || 'losses',
      order: order || 'desc',
      pageSize: pageSize || 5,
      pageIndex: pageIndex || 1,
    });

    return this.apiService.post<GetLossesBalanceResponse>(
      GET_LOSSES_ENDPOINT,
      this.getFiltersBody(query, filters),
      { params: queryParams },
    );
  }

  getLocationsLosses(params?: lossesLocationParams) {
    const { dateFrom, dateTo, filters, origin, reliabilityThreshold } =
      params ?? ({} as Partial<lossesLocationParams>);

    return this.apiService
      .post<GetLocationsLossesResponse>(GET_LOSSES_LOCATION_ENDPOINT, filters, {
        params: this.toHttpParams({
          dateFrom,
          dateTo,
          origin,
          reliabilityThreshold,
        }),
      })
      .pipe(withSpinner());
  }

  // ---------- Chart ----------

  getLossesChartData(params: lossesChartParams) {
    return this.apiService
      .get<GetCenterChartLossesDataItems>(
        GET_LOSSES_CHART_ENDPOINT,
        this.chartQueryParams(params),
      )
      .pipe(withSpinner());
  }

  postLossesChartData(params: lossesChartParams) {
    return this.apiService
      .post<GetCenterChartLossesDataItems>(
        GET_LOSSES_CHART_ENDPOINT,
        this.getFiltersBody(params.query, params.filters),
        { params: this.chartQueryParams(params) },
      )
      .pipe(withSpinner());
  }

  // ---------- Stats ----------

  getStatsLossesInfo(params?: lossesStatsParams) {
    return this.apiService
      .get<GetStatsLossesInfoResponse>(
        GET_LOSSES_STATS_ENDPOINT,
        this.statsQueryParams(params),
      )
      .pipe(withSpinner());
  }

  postStatsLossesInfo(params?: lossesStatsParams) {
    return this.apiService
      .post<GetStatsLossesInfoResponse>(
        GET_LOSSES_STATS_ENDPOINT,
        this.getFiltersBody(params?.query, params?.filters),
        { params: this.statsQueryParams(params) },
      )
      .pipe(withSpinner());
  }

  // ---------- Helpers privados ----------

  /** Construye HttpParams descartando valores null/undefined. */
  private toHttpParams(
    values: Record<string, QueryValue | null | undefined>,
  ): HttpParams {
    const clean = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== null && v !== undefined),
    ) as Record<string, QueryValue>;
    return new HttpParams().appendAll(clean);
  }

  private chartQueryParams(params: lossesChartParams): HttpParams {
    const { dateFrom, dateTo, fields, centerId, reliabilityThreshold, origin } =
      params;
    return this.toHttpParams({
      dateFrom,
      dateTo,
      reliabilityThreshold,
      origin,
      fields: Array.isArray(fields) ? fields.join(',') : fields,
      centerId: centerId || NO_CENTER,
    });
  }

  private statsQueryParams(params?: lossesStatsParams): HttpParams {
    const { centerId, dateFrom, dateTo, reliabilityThreshold, origin } =
      params ?? ({} as Partial<lossesStatsParams>);
    return this.toHttpParams({
      dateFrom,
      dateTo,
      reliabilityThreshold,
      origin,
      centerId: centerId || NO_CENTER,
    });
  }

  private getFiltersBody(
    query: string | undefined,
    filters: Filter[] | undefined,
  ) {
    if (!filters) return undefined;
    return {
      filters: filters.map((filter) => ({
        ...filter,
        operator: filter?.operator || 'equals',
      })),
      query,
    };
  }
}
